"use client";

import * as React from "react";
import Link from "next/link";
import { Check, Inbox, BrushCleaning, Newspaper, X } from "lucide-react";
import { Button } from "@/components/ui/shadcn/button";
import { ScrollArea } from "@/components/ui/shadcn/scroll-area";
import { Switch } from "@/components/ui/shadcn/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/shadcn/tooltip";
import { NotificationItem } from "./NotificationItem";
import type { NotificationDropdownProps } from "@/types/notifications";

export function NotificationDropdown({
  notifications,
  showRead,
  onToggleShowRead,
  onMarkAsRead,
  onMarkAllAsRead,
  onClearToasts,
  onNotificationClick,
  onClose,
  isLoading = false,
}: NotificationDropdownProps) {
  // Filter notifications based on showRead preference
  // Note: Toast messages always show (they're just history), only news gets filtered
  const filteredNotifications = React.useMemo(() => {
    if (showRead) {
      return notifications;
    }
    // Show all toasts + unread news
    return notifications.filter((n) => n.kind === "toast" || n.readAt === null);
  }, [notifications, showRead]);

  const unreadCount = React.useMemo(() => {
    return notifications.filter((n) => n.readAt === null && n.kind === "news").length;
  }, [notifications]);

  const toastCount = React.useMemo(() => {
    return notifications.filter((n) => n.kind === "toast").length;
  }, [notifications]);

  const hasUnread = unreadCount > 0;
  const hasToasts = toastCount > 0;
  const isEmpty = filteredNotifications.length === 0;

  return (
    <div className="flex w-full sm:w-[420px] flex-col rounded-[1.25rem] border border-border opacity-95 bg-background shadow-[0_40px_80px_-45px_rgba(8,12,28,0.75)] backdrop-blur-[28px] backdrop-saturate-[120%]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[rgba(118,127,190,0.18)] px-3 sm:px-4 py-2.5 sm:py-3.5">
        <div>
          <h3 className="text-xs sm:text-sm font-semibold text-foreground">Notifications</h3>
          {hasUnread && (
            <p className="mt-0.5 text-[10px] sm:text-xs text-muted-foreground">
              {unreadCount} unread {unreadCount === 1 ? "notification" : "notifications"}
            </p>
          )}
        </div>

        <div className="flex items-center gap-1">
          {/* Mark all as read button */}
          {hasUnread && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onMarkAllAsRead}
              className="h-7 sm:h-8 text-[10px] sm:text-xs text-muted-foreground hover:text-foreground hover:bg-accent px-2 sm:px-3"
            >
              <Check className="mr-1 sm:mr-1.5 h-3 sm:h-3.5 w-3 sm:w-3.5" />
              <span className="hidden sm:inline">Mark all read</span>
              <span className="sm:hidden">Mark all</span>
            </Button>
          )}

          {/* Close button */}
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-7 w-7 sm:h-8 sm:w-8 text-muted-foreground hover:text-foreground hover:bg-accent"
              aria-label="Close notifications"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Show read toggle */}
      <div className="flex items-center justify-between border-b border-[rgba(118,127,190,0.18)] px-3 sm:px-4 py-2 sm:py-3">
        <label htmlFor="show-read" className="text-xs sm:text-sm text-foreground cursor-pointer">
          Show read notifications
        </label>
        <div className="flex items-center gap-2">
          {hasToasts && (
            <TooltipProvider>
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClearToasts}
                    className="h-7 w-7 sm:h-8 sm:w-8 text-muted-foreground hover:text-red-400 hover:bg-accent"
                    aria-label="Clear notifications"
                  >
                    <BrushCleaning className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left" className="hidden sm:block">
                  <p>Clear notifications</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <Switch id="show-read" checked={showRead} onCheckedChange={onToggleShowRead} />
        </div>
      </div>

      {/* Notification list */}
      {isLoading ? (
        <div className="flex h-[320px] items-center justify-center">
          <div className="text-sm text-muted-foreground">Loading notifications...</div>
        </div>
      ) : isEmpty ? (
        <div className="flex h-[320px] flex-col items-center justify-center gap-3 p-8 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
            <Inbox className="h-7 w-7 text-muted-foreground" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-foreground">You&apos;re up to date</h4>
            <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
              {showRead ? "No notifications yet" : "No unread notifications"}
            </p>
          </div>
        </div>
      ) : (
        <ScrollArea className="h-[280px] sm:h-[420px]">
          <div className="p-1.5 sm:p-2 space-y-0.5">
            {filteredNotifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={onMarkAsRead}
                onClick={onNotificationClick}
              />
            ))}
          </div>
        </ScrollArea>
      )}

      {/* Footer */}
      <div className="border-t border-[rgba(118,127,190,0.18)] p-2 sm:p-3 text-center">
        <Link
          href="/news"
          className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs text-muted-foreground hover:text-background-brand transition-colors"
        >
          <Newspaper className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
          Latest News
        </Link>
      </div>
    </div>
  );
}
